import React, { useState, useEffect } from 'react';
import api from '../services/api';
import AddSitePage from './AddSitePage';

function Settings({ websites, onWebsitesChange }) {
  const [oauthStatus, setOauthStatus] = useState({ connected: false, token_expired: false });
  const [gscAccounts, setGscAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [addFormData, setAddFormData] = useState({
    url: '',
    name: '',
    type: 'url'
  });

  useEffect(() => {
    checkOAuthStatus();
    loadGscAccounts();
    api.getCurrentUser().then(d => setCurrentUser(d.user || d)).catch(() => {});
  }, []);

  const loadGscAccounts = async () => {
    setAccountsLoading(true);
    try {
      const data = await api.listGscAccounts();
      setGscAccounts(data.accounts || []);
    } catch (e) {
      console.error('Failed to load GSC accounts:', e);
    } finally {
      setAccountsLoading(false);
    }
  };

  const handleConnectAnother = async () => {
    try {
      const data = await api.getFreshGoogleAuthUrl();
      if (data.auth_url) window.location.href = data.auth_url;
    } catch (e) {
      alert('Failed to get OAuth URL: ' + e.message);
    }
  };

  const handleRemoveAccount = async (cred) => {
    const msg = cred.site_count > 0
      ? `Disconnect ${cred.google_email}? ${cred.site_count} site(s) will lose their connection.`
      : `Disconnect ${cred.google_email}?`;
    if (!window.confirm(msg)) return;
    try {
      await api.removeGscAccount(cred.id);
      await loadGscAccounts();
      onWebsitesChange();
    } catch (e) {
      alert('Failed to disconnect: ' + e.message);
    }
  };

  const checkOAuthStatus = async () => {
    try {
      const data = await api.getOAuthStatus();
      setOauthStatus(data);
    } catch (error) {
      console.error('Error checking OAuth:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectOAuth = async () => {
    try {
      const data = await api.getOAuthUrl(window.location.pathname);
      if (data.auth_url) {
        window.location.href = data.auth_url;
      }
    } catch (error) {
      alert('Failed to get OAuth URL: ' + error.message);
    }
  };

  const handleDisconnectOAuth = async () => {
    if (!window.confirm('Disconnect Google Search Console?')) return;
    
    try {
      await api.disconnectOAuth();
      alert('✅ Disconnected successfully');
      await checkOAuthStatus();
    } catch (error) {
      alert('Failed to disconnect: ' + error.message);
    }
  };

  const handleAddWebsite = async (e) => {
    e.preventDefault();
    
    let gscPropertyUrl = addFormData.url;
    if (addFormData.type === 'domain') {
      const hostname = new URL(addFormData.url).hostname.replace('www.', '');
      gscPropertyUrl = `sc-domain:${hostname}`;
    } else {
      gscPropertyUrl = addFormData.url.endsWith('/') ? addFormData.url : addFormData.url + '/';
    }

    try {
      await api.addWebsite({
        gsc_property_url: gscPropertyUrl,
        display_name: addFormData.name
      });
      alert(`✅ Website "${addFormData.name}" added successfully!`);
      setShowAddModal(false);
      setAddFormData({ url: '', name: '', type: 'url' });
      onWebsitesChange();
    } catch (error) {
      alert('Failed to add website: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteWebsite = async (id, name) => {
    if (!window.confirm(`Remove "${name}"? This will delete all keyword data.`)) return;
    
    try {
      await api.deleteWebsite(id);
      alert(`✅ Website "${name}" removed`);
      onWebsitesChange();
    } catch (error) {
      alert('Failed to remove website: ' + error.message);
    }
  };

  const handleSyncWebsite = async (id) => {
    if (!window.confirm('Sync last 28 days of data for this website?')) return;
    
    let dispatch;
    try {
      dispatch = await api.syncWebsite(id);
    } catch (error) {
      const errData = error.response?.data;
      if (errData?.action === 'reconnect_google') {
        const email = errData.google_email || 'this account';
        const doReconnect = window.confirm(
          `Google account (${email}) needs to be reconnected.\n\n` +
          `Google revoked the access token. Click OK to reconnect now, or Cancel to do it later.`
        );
        if (doReconnect) {
          try {
            const authData = await api.getOAuthUrl(window.location.pathname);
            if (authData.auth_url) {
              window.location.href = authData.auth_url;
              return;
            }
            alert('Could not get reconnect URL from server.');
          } catch (e) {
            alert('Reconnect failed to start: ' + e.message);
          }
        }
      } else {
        alert('Sync failed to start: ' + (errData?.message || errData?.error || error.message));
      }
      return;
    }
    
    // Sync dispatched, poll for completion
    const syncLogId = dispatch?.sync_log_id;
    setSyncingId(id);
    
    const startedAt = Date.now();
    const MAX_MS = 10 * 60 * 1000; // 10 minutes
    
    const poll = async () => {
      try {
        const statusResp = await api.getSyncStatus(id);
        const recent = (statusResp.recent_syncs || []).find(s => s.id === syncLogId)
                    || (statusResp.recent_syncs || [])[0];
        
        if (!recent) {
          return true; // keep polling — log not visible yet
        }
        
        if (recent.status === 'completed') {
          setSyncingId(null);
          alert(`✅ Sync Complete!\n\nRows Fetched: ${recent.rows_fetched || 0}\nKeywords Added: ${recent.rows_inserted || 0}\nKeywords Updated: ${recent.rows_updated || 0}\nDuration: ${recent.duration_seconds || 0}s`);
          onWebsitesChange();
          return false;
        }
        
        if (recent.status === 'failed') {
          setSyncingId(null);
          alert(`❌ Sync Failed\n\n${recent.error_message || 'Unknown error'}`);
          return false;
        }
        
        // still running
        if (Date.now() - startedAt > MAX_MS) {
          setSyncingId(null);
          alert('Sync is still running after 10 minutes. Check back later; it will continue in the background.');
          return false;
        }
        return true;
      } catch (e) {
        console.error('sync poll error', e);
        return true; // network hiccup — keep polling
      }
    };
    
    const interval = setInterval(async () => {
      const shouldContinue = await poll();
      if (!shouldContinue) clearInterval(interval);
    }, 3000);
    
    // Also fire an immediate poll for responsiveness
    setTimeout(async () => {
      const shouldContinue = await poll();
      if (!shouldContinue) clearInterval(interval);
    }, 1000);
  };

  // isConnected removed — replaced by gscAccounts.length check (multi-account support)

  return (
    <div>
      <h2 className="page-title">⚙️ Settings & Management</h2>

      {/* Connected Google Accounts */}
      <div className="card">
        <div className="card-header">
          <h2>🔗 Connected Google Accounts</h2>
          <button className="btn-primary" onClick={handleConnectAnother}>
            ➕ Connect Google account
          </button>
        </div>
        <div className="card-body">
          {accountsLoading ? (
            <div className="loading"><div className="spinner"></div><p>Loading accounts...</p></div>
          ) : gscAccounts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', background: '#fafafa', borderRadius: 12, border: '1px dashed #e2e8f0' }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>🔌</div>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1e293b', margin: '0 0 6px' }}>No Google accounts connected</h3>
              <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 16px', maxWidth: 380, marginLeft: 'auto', marginRight: 'auto' }}>
                Connect a Google account that has Search Console access. You can connect multiple accounts to track sites verified under different Google accounts.
              </p>
              <button onClick={handleConnectAnother}
                style={{ padding: '10px 20px', fontSize: 14, fontWeight: 600, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
                Connect your first Google account
              </button>
            </div>
          ) : (
            <div>
              {gscAccounts.map(acc => (
                <div key={acc.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 10, marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#dbeafe', color: '#1e40af', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700 }}>
                      {(acc.google_email || '?')[0].toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>
                        {acc.google_email || <em style={{ color: '#94a3b8' }}>(legacy account, no email)</em>}
                      </div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>
                        {acc.site_count} site{acc.site_count !== 1 ? 's' : ''} tracked
                        {acc.created_at && ` · connected ${new Date(acc.created_at).toLocaleDateString()}`}
                      </div>
                    </div>
                  </div>
                  <button onClick={() => handleRemoveAccount(acc)}
                    style={{ padding: '6px 12px', fontSize: 12, fontWeight: 600, background: '#fff', color: '#ef4444', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer' }}>
                    Disconnect
                  </button>
                </div>
              ))}
              <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', marginTop: 8 }}>
                Need to track a site verified under a different Google account? Click "Connect Google account" above.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Websites List */}
      <div className="card">
        <div className="card-header">
          <h2>🌐 Your Websites</h2>
          <button 
            className="btn-primary" 
            onClick={() => {
              if (gscAccounts.length === 0) {
                alert('⚠️ Please connect a Google account first.');
                return;
              }
              setShowAddModal(true);
            }}
          >
            ➕ Add Website
          </button>
        </div>
        <div className="card-body">
          {websites.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🌐</div>
              <h3>No Websites Added</h3>
              <p>Click "Add Website" to start tracking</p>
            </div>
          ) : (
            <div className="websites-list">
              {websites.map(website => (
                <div key={website.id} className="website-item">
                  <div className="website-info">
                    <h4>🌐 {website.display_name}</h4>
                    <p>{website.gsc_property_url || website.domain}</p>
                    <p className="sync-info">
                      Last synced: {website.last_synced_at ? new Date(website.last_synced_at).toLocaleString() : 'Never'}
                    </p>
                  </div>
                  <div className="website-actions">
                    <button
                      className="btn-success"
                      onClick={() => handleSyncWebsite(website.id)}
                      disabled={gscAccounts.length === 0 || syncingId === website.id}
                    >
                      {syncingId === website.id ? '⏳ Syncing...' : '🔄 Sync'}
                    </button>
                    <button 
                      className="btn-danger"
                      onClick={() => handleDeleteWebsite(website.id, website.display_name)}
                    >
                      🗑️ Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Site - Full screen experience */}
      {showAddModal && (
        <AddSitePage
          onClose={() => setShowAddModal(false)}
          onAdded={(websiteId) => {
            setShowAddModal(false);
            onWebsitesChange();
          }}
        />
      )}

      {currentUser?.is_admin && <AdminUsersPanel />}
    </div>
  );
}

// ── Admin Panel: approve / reject pending users (admin-only) ────────────────
function AdminUsersPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.listAllUsers();
      setUsers(data.users || []);
    } catch (e) {
      console.error('Failed to load users:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleAction = async (user, action) => {
    const verb = action === 'approve' ? 'Approve' : 'Reject and delete';
    if (!window.confirm(`${verb} ${user.email}?`)) return;
    setBusy(b => ({ ...b, [user.id]: true }));
    try {
      await api.approveUser(user.id, action);
      await load();
    } catch (e) {
      alert(e.response?.data?.error || 'Action failed');
    } finally {
      setBusy(b => ({ ...b, [user.id]: false }));
    }
  };

  const pending = users.filter(u => !u.is_active);
  const active = users.filter(u => u.is_active);

  return (
    <div className="card">
      <div className="card-header">
        <h2>👥 User Administration</h2>
        <button onClick={load} className="btn-secondary" style={{ fontSize: 12, padding: '6px 12px' }}>
          🔄 Refresh
        </button>
      </div>
      <div className="card-body">
        {loading ? (
          <div className="loading"><div className="spinner"></div><p>Loading users...</p></div>
        ) : (
          <>
            {/* Pending users — most important */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
                Pending approval ({pending.length})
              </div>
              {pending.length === 0 ? (
                <div style={{ padding: '16px 20px', background: '#f0fdf4', borderRadius: 8, fontSize: 13, color: '#16a34a', border: '1px solid #bbf7d0' }}>
                  ✓ No pending sign-up requests
                </div>
              ) : (
                pending.map(u => (
                  <div key={u.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, marginBottom: 8 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
                        {u.email}
                      </div>
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                        {u.full_name && <span>{u.full_name} · </span>}
                        {u.company_name && <span>{u.company_name} · </span>}
                        Requested {u.created_at ? new Date(u.created_at).toLocaleString() : 'recently'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => handleAction(u, 'approve')} disabled={busy[u.id]}
                        style={{ padding: '7px 14px', fontSize: 12, fontWeight: 600, background: '#10b981', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', opacity: busy[u.id] ? 0.5 : 1 }}>
                        ✓ Approve
                      </button>
                      <button onClick={() => handleAction(u, 'reject')} disabled={busy[u.id]}
                        style={{ padding: '7px 14px', fontSize: 12, fontWeight: 600, background: '#fff', color: '#ef4444', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer', opacity: busy[u.id] ? 0.5 : 1 }}>
                        ✕ Reject
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Active users */}
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
                Active users ({active.length})
              </div>
              {active.map(u => (
                <div key={u.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
                      {u.email}
                      {u.is_admin && <span style={{ fontSize: 10, padding: '2px 8px', background: '#dbeafe', color: '#1e40af', borderRadius: 10, fontWeight: 700 }}>ADMIN</span>}
                    </div>
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                      {u.full_name && <span>{u.full_name} · </span>}
                      {u.last_login_at ? `last login ${new Date(u.last_login_at).toLocaleDateString()}` : 'never logged in'}
                    </div>
                  </div>
                  {!u.is_admin && (
                    <button onClick={() => handleAction(u, 'reject')} disabled={busy[u.id]}
                      style={{ padding: '6px 12px', fontSize: 11, fontWeight: 600, background: '#fff', color: '#ef4444', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer', opacity: busy[u.id] ? 0.5 : 1 }}>
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Settings;
